import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAnimal, getImageUrl, extractCityFromAddress, loadMatchSession } from '@rehome/shared';
import type { Animal } from '@rehome/shared';
import {
  ArrowLeft, Calendar, MapPin, HeartHandshake, ChevronLeft, ChevronRight,
  Activity, Cake, Weight, ClipboardList, PawPrint, Palette, HeartPulse, Sparkles,
} from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  Available: 'Gazdikereső', AdoptionPending: 'Örökbefogadás folyamatban', Adopted: 'Örökbefogadva',
  Foster: 'Ideiglenesben',
  Medical: 'Orvosi kezelés alatt', StrayHold: 'Várakozási idő', Quarantine: 'Karantén',
};
const STATUS_STYLES: Record<string, string> = {
  Available: 'bg-emerald-100 text-emerald-800', AdoptionPending: 'bg-amber-100 text-amber-800',
  Adopted: 'bg-blue-100 text-blue-800',
  Foster: 'bg-purple-100 text-purple-800', Medical: 'bg-amber-100 text-amber-800',
  StrayHold: 'bg-red-100 text-red-800', Quarantine: 'bg-orange-100 text-orange-800',
};

const ACTIVITY_LABELS: Record<string, string> = {
  low: 'Alacsony', medium: 'Közepes', high: 'Magas', very_high: 'Nagyon aktív',
};

const GENDER_LABELS: Record<string, string> = { Male: 'Kan', Female: 'Nőstény' };

/** Full animal profile page with gallery, structured info table, and adoption CTA. */
const AnimalProfilePage: React.FC = () => {
  const { animalId } = useParams<{ animalId: string }>();
  const navigate = useNavigate();
  const { animal, loading } = useAnimal(animalId);
  const [galleryIdx, setGalleryIdx] = useState(0);

  // Ha a felhasználó egy Smart Match találatból érkezett ide, adjunk neki
  // egyértelmű utat vissza az eredményekhez. A matching a böngészőben marad
  // (l. shared/src/matchSession.ts), így ez a bejelentkezési kitérő után is
  // működik — pont ez volt korábban a zsákutca.
  const matchSession = useMemo(() => loadMatchSession(), []);
  const isMatchResult = !!matchSession?.results.some(r => r.animalId === animalId);

  // While the list is loading, don't flash the "not found" screen
  if (loading && !animal) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 animate-pulse">
        <div className="h-72 sm:h-96 bg-gray-200 rounded-2xl" />
        <div className="h-40 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!animal) {
    return (
      <div className="text-center py-16">
        <PawPrint className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Állat nem található</h2>
        <p className="text-gray-500">A keresett állat nem létezik vagy már örökbe fogadták.</p>
        <button onClick={() => navigate('/animals')} className="mt-4 text-teal-600 font-semibold hover:underline">
          ← Vissza a listához
        </button>
      </div>
    );
  }

  // A `gallery` MINDEN publikus képet tartalmaz (a borítót is) — l. a
  // 042-es migráció 5. pontját. A `getImageUrl` csak a migráció előtti
  // sorok miatt marad tartalékként, ha a tömb üresen maradt volna.
  // Privát kép ide sosem kerül: a platform csak publikus URL-t lát.
  const gallery = (animal.gallery?.length ? animal.gallery : [getImageUrl(animal)]).filter(Boolean) as string[];
  const nextImage = () => { if (gallery.length > 1) setGalleryIdx((galleryIdx + 1) % gallery.length); };
  const prevImage = () => { if (gallery.length > 1) setGalleryIdx((galleryIdx - 1 + gallery.length) % gallery.length); };
  const birthYear = animal.birthDate ? new Date(animal.birthDate).getFullYear() : null;
  const approximateAge = animal.age != null ? `${animal.age} éves` : birthYear ? `Szül: ${birthYear}` : 'Ismeretlen kor';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <button onClick={() => navigate('/animals')} className="flex items-center gap-2 text-gray-500 hover:text-teal-700">
          <ArrowLeft className="w-4 h-4" /> Vissza a listához
        </button>
        {isMatchResult && (
          <Link to="/match" className="flex items-center gap-2 text-teal-600 font-medium hover:text-teal-800">
            <Sparkles className="w-4 h-4" /> Vissza a Smart Match találataidhoz
          </Link>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* ── Image Gallery ─────────────────────────────── */}
        <div className="relative h-72 sm:h-96 bg-gray-200">
          {gallery.length > 0 ? (
            <>
              <img src={gallery[galleryIdx]} className="w-full h-full object-cover" alt={animal.name} />
              {gallery.length > 1 && (
                <>
                  <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow hover:bg-white"><ChevronLeft className="w-5 h-5 text-gray-700" /></button>
                  <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow hover:bg-white"><ChevronRight className="w-5 h-5 text-gray-700" /></button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {gallery.map((_, i) => <button key={i} onClick={() => setGalleryIdx(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === galleryIdx ? 'bg-white w-6' : 'bg-white/50'}`} />)}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400"><PawPrint className="w-16 h-16" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-6 pointer-events-none">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{animal.name}</h1>
              <p className="text-white/80">{animal.breed} · {animal.species} · {GENDER_LABELS[animal.gender || ''] || animal.gender || 'Ismeretlen nem'}</p>
            </div>
          </div>
        </div>

        {/* ── Content Grid ──────────────────────────────── */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_STYLES[animal.status] || 'bg-gray-100'}`}>{STATUS_LABELS[animal.status] || animal.status}</span>
              <span className="text-sm text-gray-500 flex items-center gap-1"><Calendar className="w-4 h-4" /> Bekerült: {animal.intakeDate}</span>
            </div>

            {animal.bio ? (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Bemutatkozás</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{animal.bio}</p>
              </div>
            ) : (
              <p className="text-gray-400 italic">{animal.name} szerető otthonra vágyik. Ismerd meg őt közelebbről!</p>
            )}

            {(animal.traits || []).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><ClipboardList className="w-4 h-4" /> Tulajdonságok</h3>
                <div className="flex flex-wrap gap-2">{(animal.traits || []).map((t, i) => <span key={i} className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">{t}</span>)}</div>
              </div>
            )}

            {animal.status === 'Available' ? (
              <button onClick={() => navigate(`/adoption/${animal.id}`)} className="w-full sm:w-auto bg-teal-600 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-teal-200/50">
                <HeartHandshake className="w-5 h-5" /> Örökbefogadom
              </button>
            ) : (
              <p className="text-sm text-gray-400 italic bg-gray-50 rounded-xl p-4">Ez az állat jelenleg nem örökbefogadható (státusz: {STATUS_LABELS[animal.status] || animal.status}).</p>
            )}
          </div>

          {/* ── Structured Info ─────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Adatok</h3>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3 text-sm">
              <InfoRow icon={<Cake className="w-4 h-4" />} label="Életkor" value={approximateAge} />
              {animal.species && <InfoRow icon={<PawPrint className="w-4 h-4" />} label="Faj" value={animal.species} />}
              {animal.breed && <InfoRow icon={<ClipboardList className="w-4 h-4" />} label="Fajta" value={animal.breed} />}
              {animal.gender && <InfoRow icon={<UsersIcon className="w-4 h-4" />} label="Nem" value={GENDER_LABELS[animal.gender] || animal.gender} />}
              {animal.weightKg != null && <InfoRow icon={<Weight className="w-4 h-4" />} label="Súly" value={`${animal.weightKg} kg`} />}
              {animal.color && <InfoRow icon={<Palette className="w-4 h-4" />} label="Szín" value={animal.color} />}
              <InfoRow icon={<Activity className="w-4 h-4" />} label="Aktivitás" value={ACTIVITY_LABELS[animal.activityLevel || ''] || animal.activityLevel || 'Nincs megadva'} />
              {animal.intakeType && <InfoRow icon={<MapPin className="w-4 h-4" />} label="Bekerülés" value={animal.intakeType} />}
              {animal.birthDate && <InfoRow icon={<Cake className="w-4 h-4" />} label="Születési dátum" value={animal.birthDate} />}
              {animal.microchipId && <InfoRow icon={<SyringeIcon className="w-4 h-4" />} label="Chip" value={animal.microchipId} />}
            </div>

            {(animal.isVaccinated != null || animal.isNeutered != null || animal.isDewormed != null) && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1"><HeartPulse className="w-4 h-4" /> Egészségi állapot</h4>
                <div className="flex flex-wrap gap-2">
                  {animal.isVaccinated != null && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${animal.isVaccinated ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {animal.isVaccinated ? 'Oltott' : 'Nincs oltva'}
                    </span>
                  )}
                  {animal.isNeutered != null && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${animal.isNeutered ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {animal.isNeutered ? 'Ivartalanítva' : 'Nincs ivartalanítva'}
                    </span>
                  )}
                  {animal.isDewormed != null && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${animal.isDewormed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {animal.isDewormed ? 'Parazitamentes' : 'Nem parazitamentes'}
                    </span>
                  )}
                </div>
              </div>
            )}

            {animal.foundLocation && (
              <div className="bg-amber-50 rounded-xl p-4 mt-4">
                <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1"><MapPin className="w-4 h-4" /> Megtalálás helye</h4>
                {/* Only the city is shown publicly — the exact street/house
                    number is not exposed here (see AnimalProfilePage admin
                    for the full address). */}
                <p className="text-sm text-amber-700">{extractCityFromAddress(animal.foundLocation.address) || 'Ismeretlen település'}</p>
                {animal.foundLocation.foundDate && <p className="text-xs text-amber-600 mt-1">{animal.foundLocation.foundDate}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-gray-400 mt-0.5 flex-shrink-0">{icon}</div>
    <div className="min-w-0"><p className="text-xs text-gray-400">{label}</p><p className="text-gray-800 font-medium truncate">{value || '—'}</p></div>
  </div>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SyringeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2l4 4" /><path d="M17 7l3-3" /><path d="M19 9L8.7 19.3c-1 1-2.5 1-3.4 0l-.6-.6c-1-1-1-2.5 0-3.4L15 5" /><path d="M9 11l4 4" /><path d="M5 19l-3 3" /><path d="M14 4l6 6" />
  </svg>
);

export default AnimalProfilePage;
